import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  Input,
  model,
  ModelSignal,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { DatePipe } from "@angular/common";
import { BreakpointService } from "@bitwarden/web-vault/app/shared/breakpoints";
import { DateRange } from "@angular/material/datepicker";
import { distinctUntilKeyChanged, filter } from "rxjs";
import { addDays, startOfDay } from "date-fns";
import {
  defaultGroupForCode,
  type ISymbolView,
  SymbolView,
} from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
import { SymbolGroupIcon } from "@bitwarden/web-vault/app/models/types/symbol-group.types";
import { OnBackDirective } from "@bitwarden/web-vault/app/directives/onBack.directive";
import {
  IReferenceData,
  ReferenceDataSource,
} from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { MatDialog } from "@angular/material/dialog";
import { PriceDetailsComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-symbols/price-details/price-details.component";
import { DeviceDetectorService } from "ngx-device-detector";
import {
  DESKTOP_DIALOG,
  MOBILE_DIALOG,
} from "@bitwarden/web-vault/app/models/constants/global.constants";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { PICKER_TYPE } from "@bitwarden/web-vault/app/directives/views-datepicker.directive";
import { EditSymbolFormComponent } from "@bitwarden/web-vault/app/gloss/manage/manage-symbols/symbol-form/edit-symbol-form.component";
import { TIME_DIRECTION_AGO } from "@bitwarden/web-vault/app/components/date-picker/calendar/calendar.component";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { ScenarioStoreService } from "@bitwarden/web-vault/app/services/store/scenario/scenario.store.service";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { isReadOnlySymbol } from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";

export interface ManualReferenceDataPartition<T extends { source: string }> {
  manual: T[];
  hasNonManual: boolean;
}

// Must stay >= the .symbol-right-panel exit animation duration (--duration-normal,
// currently 200ms, see material-ui.scss) so the fallback never fires before a normal
// animationend close would.
const EDIT_DETAILS_CLOSE_FALLBACK_MS = 350;

// Pure and exported so the delete-fallback decision (which reference data is safe to delete,
// and whether some non-manual data is left behind) can be unit tested without instantiating
// the component - see symbol-details.component.spec.ts.
export function partitionManualReferenceData<
  T extends { base: string; symbol: string; source: string },
>(allRefData: T[], symbolCode: string): ManualReferenceDataPartition<T> {
  const matching =
    allRefData?.filter((rd) => rd.base === symbolCode || rd.symbol === symbolCode) ?? [];
  const manual = matching.filter((rd) => rd.source === ReferenceDataSource.MANUAL);
  return { manual, hasNonManual: manual.length < matching.length };
}

@Component({
  selector: "app-symbol-details",
  templateUrl: "./symbol-details.component.html",
})
export class SymbolDetailsComponent extends OnBackDirective {
  @Output() onSymbolUpdate: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild("startPickerTrigger", { read: ElementRef }) startPickerTrigger!: ElementRef;
  @ViewChild("endPickerTrigger", { read: ElementRef }) endPickerTrigger!: ElementRef;
  @ViewChild("startCalendar", { read: ElementRef }) startCalendar?: ElementRef;
  @ViewChild("endCalendar", { read: ElementRef }) endCalendar?: ElementRef;
  @ViewChild("detailsFormEl") detailsFormEl?: EditSymbolFormComponent;
  @ViewChild("priceFormEl") priceFormEl?: EditSymbolFormComponent;

  protected newSymbolCode: string = "";
  private transactionStore = inject(TransactionStoreService);
  private i18nService = inject(I18nService);
  private dialog: MatDialog = inject(MatDialog);
  protected deviceService = inject(DeviceDetectorService);
  private isLgOrSmaller = inject(BreakpointService).isLgOrSmaller;
  /** Same semantics as `DetailsWrapperComponent.showMobileLayout`: the LG breakpoint only
   *  applies while shown as an overlay panel. */
  @Input() isOverlay = true;
  protected get showMobileLayout(): boolean {
    return this.deviceService.isMobile() || (this.isOverlay && this.isLgOrSmaller());
  }
  private confirmationService = inject(ConfirmationDialogService);
  private scenarioStore = inject(ScenarioStoreService);
  private datePipe = inject(DatePipe);
  private elementRef = inject(ElementRef);
  private injector = inject(Injector);

  private startPickerAdjustment = 0;
  private endPickerAdjustment = 0;

  protected symbol: ModelSignal<ISymbolView | undefined> = model<ISymbolView | undefined>(
    undefined,
  );
  protected isEditDetailsModalOpen: boolean = false;
  protected isEditDetailsClosing: boolean = false;
  protected isAddPriceModalOpen: boolean = false;
  protected isAddPriceClosing: boolean = false;
  private editDetailsCloseTimer: ReturnType<typeof setTimeout> | null = null;

  protected isReadOnlySymbol = computed(() =>
    isReadOnlySymbol(this.symbol()?.code, this.symbol()?.sources),
  );

  protected symbolComputedInitial = computed(() => {
    return this.transactionStore.symbols.collection()?.find((s) => s.code === this.symbol()?.code);
  });

  // Blocks on the held asset (actualQuantity.symbol) as well as valuation/conversion roles
  // (currency, convsym): normalization uses currency/convsym to price the transaction, so
  // deleting a symbol used only in those roles would still break that transaction's revaluation
  // even though it was never the held asset.
  protected linkedTransactions = computed(() => {
    const code = this.symbol()?.code;
    if (!code) {
      return [];
    }
    return (this.transactionStore.enrichTransaction.collection() ?? []).filter(
      (transaction) =>
        transaction.definition !== TransactionStatusEnum.empty_account_balance &&
        transaction.definition !== TransactionStatusEnum.fake &&
        (transaction.quantity?.actualQuantity?.symbol === code ||
          transaction.quantity?.currency === code ||
          transaction.quantity?.convsym === code),
    );
  });

  protected linkedTransactionCount = computed(() => this.linkedTransactions().length);

  // Accounts linked to this symbol, derived from the accounts holding the linked transactions
  // (an account's currency doesn't match manual/non-ISO symbol codes, so it can't be used directly).
  protected linkedAccountCount = computed(
    () =>
      new Set(
        this.linkedTransactions()
          .map((t) => t.accountLink?.id)
          .filter(Boolean),
      ).size,
  );

  // Estimates (recurring/revaluation/transaction projections) reference a symbol both on the
  // top-level Estimate and, once a generator produces the underlying action, on that action's own
  // transactionTemplate — these two records aren't kept in sync with each other, so both need checking.
  protected linkedEstimateCount = computed(() => {
    const code = this.symbol()?.code;
    if (!code) {
      return 0;
    }
    const linkedEstimates = (this.scenarioStore.estimate.estimateView() ?? []).filter(
      (estimate) => estimate.symbol === code,
    );
    const linkedEstimateActions = (
      this.scenarioStore.estimateAction.estimateActionView() ?? []
    ).filter(
      (action) =>
        action.transactionTemplate?.symbol === code ||
        action.transactionTemplate?.currency === code,
    );
    return linkedEstimates.length + linkedEstimateActions.length;
  });

  protected linkedScenarioCount = computed(() => {
    const code = this.symbol()?.code;
    if (!code) {
      return 0;
    }
    return (this.scenarioStore.scenario.scenarioView() ?? []).filter(
      (scenario) => scenario.symbol === code,
    ).length;
  });

  protected displayIcon = computed(() => {
    const matchedRecord = this.symbolComputedInitial();
    const code = this.symbol()?.code ?? "";
    const group = matchedRecord?.group ?? defaultGroupForCode(code);
    return SymbolGroupIcon[group];
  });

  protected displayCode = computed(() => {
    return this.symbol()?.code || "";
  });

  protected readonly PICKER_TYPE = PICKER_TYPE;
  protected readonly AGO = TIME_DIRECTION_AGO;

  protected showStartPicker = signal(false);
  protected showEndPicker = signal(false);

  // Limits selectable range to 30 days to cap the number of history entries loaded at once.
  private readonly MAX_RANGE_DAYS = 30;
  private readonly DEFAULT_LOOKBACK_DAYS = 7;

  private static todayAtMidnight(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private static todayEndOfDay(): Date {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private startDate = signal<Date>(
    addDays(SymbolDetailsComponent.todayAtMidnight(), -this.DEFAULT_LOOKBACK_DAYS),
  );
  private endDate = signal<Date>(SymbolDetailsComponent.todayAtMidnight());
  private defaultStartDate = signal<Date>(
    addDays(SymbolDetailsComponent.todayAtMidnight(), -this.DEFAULT_LOOKBACK_DAYS),
  );
  private defaultEndDate = signal<Date>(SymbolDetailsComponent.todayAtMidnight());

  protected allHistory = computed(() => this.symbolComputedInitial()?.history ?? []);

  protected filteredHistory = computed(() => {
    const start = this.datePipe.transform(this.startDate(), "yyyy-MM-dd") ?? "";
    const end = this.datePipe.transform(this.endDate(), "yyyy-MM-dd") ?? "";
    return this.allHistory().filter((item) => item.date.date >= start && item.date.date <= end);
  });

  protected dateRange = computed(() => new DateRange(this.startDate(), this.endDate()));

  protected originalDateRange = computed(
    () => new DateRange(this.defaultStartDate(), this.defaultEndDate()),
  );

  protected displayDateText = computed(() => ({
    startDate: this.datePipe.transform(this.startDate(), "yyyy-MM-dd"),
    endDate: this.datePipe.transform(this.endDate(), "yyyy-MM-dd"),
  }));

  protected startDateFilter = computed(() => {
    const end = this.endDate();
    // -(MAX_RANGE_DAYS - 1) keeps the inclusive span at 30 calendar days, not 31.
    const minStart = addDays(end, -(this.MAX_RANGE_DAYS - 1));
    return (date: Date) =>
      date <= end && date >= minStart && date <= SymbolDetailsComponent.todayEndOfDay();
  });

  protected endDateFilter = computed(() => {
    const start = this.startDate();
    const maxEnd = addDays(start, this.MAX_RANGE_DAYS - 1);
    return (date: Date) =>
      date >= start && date <= maxEnd && date <= SymbolDetailsComponent.todayEndOfDay();
  });

  // Stored styles avoid calling getBoundingClientRect on every change-detection cycle.
  protected startPickerStyle = signal<Record<string, string>>({ "z-index": "20" });
  protected endPickerStyle = signal<Record<string, string>>({ "z-index": "20" });

  get isPickerOpen(): boolean {
    return this.showStartPicker() || this.showEndPicker();
  }

  constructor() {
    super();
    toObservable(this.symbolComputedInitial)
      .pipe(
        filter((sym): sym is SymbolView => !!sym && sym.history?.length > 0),
        distinctUntilKeyChanged("code"),
        takeUntilDestroyed(),
      )
      .subscribe((sym) => {
        this.initDefaultDateRange(sym.history, sym.code);
      });
    inject(DestroyRef).onDestroy(() => this.clearEditDetailsCloseTimer());
  }

  private readonly PICKER_SCALE = 0.85;
  private readonly PICKER_GAP = 8;
  private readonly MOBILE_PICKER_GAP = 2;

  private computePickerStyle(
    rect: DOMRect,
    align: "left" | "right" | "center",
    adjustment = 0,
  ): Record<string, string> {
    const scale = this.PICKER_SCALE;
    const isMobile = this.deviceService.isMobile();
    const gap = isMobile ? this.MOBILE_PICKER_GAP : this.PICKER_GAP;
    const viewportHeight = isMobile
      ? (window.visualViewport?.height ?? window.innerHeight)
      : window.innerHeight;
    const spaceAbove = rect.top - gap;
    const spaceBelow = viewportHeight - rect.bottom - gap;

    const effectiveAlign = isMobile ? "center" : align;
    const horizontal: Record<string, string> =
      effectiveAlign === "center"
        ? { left: "50%", transform: `translateX(-50%) scale(${scale})` }
        : effectiveAlign === "left"
          ? { left: `${rect.left}px`, transform: `scale(${scale})` }
          : { right: `${window.innerWidth - rect.right}px`, transform: `scale(${scale})` };

    const base: Record<string, string> = {
      display: "block",
      position: "fixed",
      ...horizontal,
      "overflow-y": "auto",
      "z-index": "20",
    };

    if (spaceAbove >= spaceBelow) {
      return {
        ...base,
        bottom: `${viewportHeight - rect.top + gap - adjustment}px`,
        "transform-origin": `bottom ${effectiveAlign === "center" ? "center" : effectiveAlign}`,
        "max-height": `${spaceAbove / scale}px`,
      };
    }
    return {
      ...base,
      top: `${rect.bottom + gap}px`,
      "transform-origin": `top ${effectiveAlign === "center" ? "center" : effectiveAlign}`,
      "max-height": `${spaceBelow / scale}px`,
    };
  }

  // Recomputes and stores picker style signals from trigger rects. Safe to call at any time
  // since the trigger elements are always rendered; the calendar elements are measured
  // separately in refreshPickerAdjustments after they mount.
  private updatePickerStyles(): void {
    if (this.showStartPicker() && this.startPickerTrigger) {
      const rect: DOMRect = this.startPickerTrigger.nativeElement.getBoundingClientRect();
      this.startPickerStyle.set(this.computePickerStyle(rect, "left", this.startPickerAdjustment));
    }
    if (this.showEndPicker() && this.endPickerTrigger) {
      const rect: DOMRect = this.endPickerTrigger.nativeElement.getBoundingClientRect();
      this.endPickerStyle.set(this.computePickerStyle(rect, "right", this.endPickerAdjustment));
    }
  }

  // Waits for the calendar to render (afterNextRender), measures its actual height, and
  // updates the stored style signals only when the adjustment changes meaningfully.
  private refreshPickerAdjustments(): void {
    afterNextRender(
      () => {
        let changed = false;

        const startAdj = this.computePickerAdjustment(this.startCalendar);
        if (Math.abs(startAdj - this.startPickerAdjustment) > 0.5) {
          this.startPickerAdjustment = startAdj;
          changed = true;
        }

        const endAdj = this.computePickerAdjustment(this.endCalendar);
        if (Math.abs(endAdj - this.endPickerAdjustment) > 0.5) {
          this.endPickerAdjustment = endAdj;
          changed = true;
        }

        if (changed) {
          this.updatePickerStyles();
        }
      },
      { injector: this.injector },
    );
  }

  private computePickerAdjustment(calendarRef: ElementRef | undefined): number {
    if (!calendarRef) {
      return 0;
    }
    const hostEl: HTMLElement = calendarRef.nativeElement;
    const children = Array.from(hostEl.children);
    if (children.length === 0) {
      return 0;
    }
    const innerH = Math.max(...children.map((c) => c.getBoundingClientRect().height));
    const hostH = hostEl.getBoundingClientRect().height;
    return Math.max(0, hostH - innerH);
  }

  private initDefaultDateRange(hist: IReferenceData[], code: string): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parseLocal = (s: string): Date => {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d);
    };
    const earliestHistoryDate = hist.reduce((min, item) => {
      const d = parseLocal(item.date.date);
      return d < min ? d : min;
    }, parseLocal(hist[0].date.date));

    const symbolTransactions = (this.transactionStore.transaction.transactionViews() ?? []).filter(
      (t) => t.quantity?.actualQuantity?.symbol === code,
    );
    const firstUsedDate = symbolTransactions.length
      ? symbolTransactions.reduce(
          (min, t) => (t.postDate.getTime() < min.getTime() ? t.postDate : min),
          symbolTransactions[0].postDate,
        )
      : undefined;

    // Currencies with no transactions of their own (e.g. the home/base currency) fall back
    // to the earliest fetched price history instead. postDate carries a time-of-day
    // (enrichTransactionDate defaults it to 23:59:59), so compare and store at day granularity
    // or a transaction posted today reads as future-dated. A genuinely future-dated transaction
    // clamps to the default lookback window instead of the full history, since dumping years of
    // history for bad data would exceed MAX_RANGE_DAYS and trap the date pickers.
    const firstUsedDay = firstUsedDate ? startOfDay(firstUsedDate) : undefined;
    const effectiveStart = !firstUsedDay
      ? earliestHistoryDate
      : firstUsedDay <= today
        ? firstUsedDay
        : addDays(today, -this.DEFAULT_LOOKBACK_DAYS);

    this.defaultStartDate.set(effectiveStart);
    this.defaultEndDate.set(today);
    this.startDate.set(effectiveStart);
    this.endDate.set(today);

    // The bound symbol changed underneath any in-progress edit; drop it rather than
    // leave a picker open against another symbol's data.
    this.closeAllPickers();
    this.startPickerAdjustment = 0;
    this.endPickerAdjustment = 0;
  }

  onDateChange(event: DateRange<Date>): void {
    if (event.start) {
      this.startDate.set(event.start);
    }
    if (event.end) {
      this.endDate.set(event.end);
    }
  }

  onStartPickerConfirm(): void {
    this.showStartPicker.set(false);
    this.showEndPicker.set(true);
    this.updatePickerStyles();
    this.refreshPickerAdjustments();
  }

  onEndPickerConfirm(): void {
    this.showEndPicker.set(false);
  }

  onPickerCancel(): void {
    this.closeAllPickers();
  }

  private closeAllPickers(): void {
    this.showStartPicker.set(false);
    this.showEndPicker.set(false);
  }

  togglePickersVisibility(pickerType: (typeof PICKER_TYPE)[keyof typeof PICKER_TYPE]): void {
    switch (pickerType) {
      case PICKER_TYPE.START:
        this.showEndPicker.set(false);
        this.showStartPicker.update((v) => !v);
        if (this.showStartPicker()) {
          this.updatePickerStyles();
          this.refreshPickerAdjustments();
        }
        break;
      case PICKER_TYPE.END:
        this.showStartPicker.set(false);
        this.showEndPicker.update((v) => !v);
        if (this.showEndPicker()) {
          this.updatePickerStyles();
          this.refreshPickerAdjustments();
        }
        break;
      case PICKER_TYPE.BOTH:
        this.closeAllPickers();
        break;
    }
  }

  @HostListener("document:mouseup", ["$event"])
  onClickOutside(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.closeAllPickers();
    }
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    if (this.isPickerOpen) {
      this.updatePickerStyles();
    }
  }

  getId(item: IReferenceData) {
    return `${item.date.date}|${item.base}|${item.symbol}`;
  }

  openEditDetailsModal(): void {
    this.clearEditDetailsCloseTimer();
    this.isEditDetailsClosing = false;
    this.isEditDetailsModalOpen = true;
  }

  closeEditDetailsModal(): void {
    if (this.isEditDetailsClosing) {
      return;
    }
    this.isEditDetailsClosing = true;
    if (this.deviceService.isMobile()) {
      // Mobile renders the edit view as a full page (no exit animation) - nothing to wait for.
      this.finishCloseEditDetails();
      return;
    }
    // Fallback: complete close if animationend never fires (element temporarily hidden, CSS load failure, etc.)
    this.editDetailsCloseTimer = setTimeout(
      () => this.finishCloseEditDetails(),
      EDIT_DETAILS_CLOSE_FALLBACK_MS,
    );
  }

  onEditDetailsPanelAnimationEnd(event: AnimationEvent): void {
    if (this.isEditDetailsClosing && event.target === event.currentTarget) {
      this.finishCloseEditDetails();
    }
  }

  private clearEditDetailsCloseTimer(): void {
    if (this.editDetailsCloseTimer !== null) {
      clearTimeout(this.editDetailsCloseTimer);
      this.editDetailsCloseTimer = null;
    }
  }

  private finishCloseEditDetails(): void {
    this.clearEditDetailsCloseTimer();
    this.isEditDetailsModalOpen = false;
    this.isEditDetailsClosing = false;
  }

  openAddPriceModal(): void {
    this.isAddPriceModalOpen = true;
  }

  closeAddPriceModal(): void {
    if (this.deviceService.isMobile()) {
      this.isAddPriceModalOpen = false;
      return;
    }
    this.isAddPriceClosing = true;
  }

  onAddPricePanelAnimationEnd(event: AnimationEvent): void {
    if (this.isAddPriceClosing && event.target === event.currentTarget) {
      this.isAddPriceModalOpen = false;
      this.isAddPriceClosing = false;
    }
  }

  async saveDetailsAndClose(formRef?: EditSymbolFormComponent): Promise<void> {
    const form = formRef ?? this.detailsFormEl;
    if (!form) {
      this.confirmationService.notify("error", "error", "somethingWentWrong");
      return;
    }
    try {
      await form.saveDetails();
    } catch {
      this.confirmationService.notify("error", "error", "somethingWentWrong");
      return;
    }
    this.closeEditDetailsModal();
  }

  async addPriceAndClose(formRef?: EditSymbolFormComponent): Promise<void> {
    const form = formRef ?? this.priceFormEl;
    if (!form) {
      this.confirmationService.notify("error", "error", "somethingWentWrong");
      return;
    }
    try {
      await form.addPrice();
    } catch {
      this.confirmationService.notify("error", "error", "somethingWentWrong");
      return;
    }
    this.closeAddPriceModal();
  }

  showPriceDetails(price: IReferenceData): void {
    const dialogRef = this.dialog.open(DialogWrapperComponent, {
      panelClass: this.deviceService.isMobile() ? MOBILE_DIALOG : DESKTOP_DIALOG,
      maxWidth: this.deviceService.isMobile() ? "100vw" : "500px",
      data: {
        title: "priceHistory",
        showBackButton: false,
        component: PriceDetailsComponent,
        componentInputs: { price, closeDialog: () => dialogRef.close() },
        showCloseButton: true,
        isInfoDialog: false,
      },
      disableClose: true,
    });
  }

  onSymbolUpdated(event: { oldCode: string; newCode: string }): void {
    this.newSymbolCode = event.newCode;
    this.onSymbolUpdate.emit(this.newSymbolCode);
  }

  getRelativeTime(dateStr: string): string {
    if (!dateStr) {
      return "";
    }
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return "today";
    }
    if (diffDays === 1) {
      return "1 day ago";
    }
    if (diffDays < 30) {
      return `${diffDays} days ago`;
    }
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) {
      return "1 month ago";
    }
    if (diffMonths < 12) {
      return `${diffMonths} months ago`;
    }
    const diffYears = Math.floor(diffMonths / 12);
    if (diffYears === 1) {
      return "1 year ago";
    }
    return `${diffYears} years ago`;
  }

  getBadgeClass(source: string): string {
    const lower = (source || "").toLowerCase();
    if (lower === "manual" || lower.includes("manual")) {
      return "tw-bg-[#6E56CF33] tw-text-[#6E56CF] tw-rounded-xl";
    }
    return "tw-text-dt-custom-teal-11 tw-bg-[#00C4C433] tw-rounded-xl";
  }

  getSourceLabel(source: string): string {
    if (!source) {
      return "";
    }
    const lower = source.toLowerCase();
    if (lower === "manual" || lower.includes("manual")) {
      return "Manually Entered";
    }
    if (
      lower.includes("gloss-fx") ||
      lower.includes("eodhd") ||
      lower.includes("auto") ||
      lower.includes("fetch")
    ) {
      return "Auto Fetch";
    }
    return source;
  }

  async deleteSymbol(): Promise<void> {
    const symbolCode = this.symbol()?.code;
    if (!symbolCode) {
      return;
    }

    const loadedSymbols = this.transactionStore.symbols.loadedCollection();
    const vaultSymbol = loadedSymbols?.find((s) => s.code === symbolCode);

    if (!vaultSymbol) {
      // No vault record for this symbol (e.g. its Symbol write silently failed while its
      // price/reference-data write succeeded) - it only exists via the generated/reference-data
      // path. There's nothing in the vault to delete, so remove the reference data that's
      // actually keeping it visible instead of refusing outright. Only ever do this for
      // manually-entered data - FOREX rates and EODHD/plugin-synced data aren't user-owned
      // (they're shared exchange-rate/market history other calculations depend on), so leave
      // those alone and refuse instead.
      const allRefData = this.transactionStore.referenceData.referenceDataViews();
      const { manual: manualRefData, hasNonManual } = partitionManualReferenceData(
        allRefData ?? [],
        symbolCode,
      );

      if (manualRefData.length === 0) {
        this.confirmationService.notify(
          "warning",
          "Cannot Delete",
          "This symbol cannot be deleted as it is derived from reference data only.",
          false,
        );
        return;
      }

      await this.transactionStore.referenceData.deleteFromVault(manualRefData);

      // If non-manual (FOREX/EODHD) data remains for this code, the generated collection will
      // keep producing this symbol even though its manual data is gone - tell the user rather
      // than silently navigating back as if it were fully removed.
      if (hasNonManual) {
        this.confirmationService.notify(
          "warning",
          "Partially Deleted",
          "Manually-entered price history was removed, but this asset also has synced price data that can't be deleted here, so it may still appear in the list.",
          false,
        );
      }

      this.handleBackClick();
      return;
    }

    if (this.linkedTransactionCount() > 0) {
      this.confirmationService.notify(
        NotificationEnum.warning,
        this.i18nService.t("notice"),
        this.i18nService.t(
          "symbolCannotDeleteWhenInUse",
          this.linkedTransactionCount(),
          this.linkedAccountCount(),
        ),
        false,
      );
      return;
    }

    if (this.linkedEstimateCount() > 0 || this.linkedScenarioCount() > 0) {
      this.confirmationService.notify(
        NotificationEnum.warning,
        this.i18nService.t("notice"),
        this.i18nService.t(
          "symbolCannotDeleteWhenLinkedToPlanning",
          this.linkedEstimateCount(),
          this.linkedScenarioCount(),
        ),
        false,
      );
      return;
    }

    const confirmed = await this.confirmationService.confirmFor(ConfirmationEnum.deletingSymbol);
    if (!confirmed) {
      return;
    }

    const deleted = await this.transactionStore.deleteSymbol(vaultSymbol);
    if (!deleted) {
      this.confirmationService.notify(NotificationEnum.error, "error", "symbolDeleteFailed");
      return;
    }

    this.confirmationService.notify(
      NotificationEnum.success,
      "succeeded",
      "symbolDeletedSuccessfully",
    );
    this.handleBackClick();
  }
}
